import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { isPlatformAdmin } from '../rbac/roles';
import type { AccountClaims, AvailableAccount } from '../types/accounts';

/**
 * Bridges the AuthContext user object (which holds the decoded JWT) to the
 * typed AccountClaims surface used by the account-layer UI.
 *
 * The User type itself is intentionally permissive (the backend keeps
 * adding fields), so we do the field plumbing here in one place.
 */
export function useAccountClaims(): AccountClaims {
  const { user } = useAuth();
  const u = (user ?? {}) as unknown as Record<string, unknown>;

  return useMemo<AccountClaims>(() => {
    const rawAvail = (u.availableAccounts ?? u.available_accounts ?? []) as
      | AvailableAccount[]
      | undefined;
    const planFeatures = (u.planFeatures ?? u.plan_features ?? []) as string[];

    return {
      accountId: (u.accountId ?? u.account_id) as string | undefined,
      currentAccountId: (u.currentAccountId ?? u.current_account_id) as string | undefined,
      availableAccounts: Array.isArray(rawAvail) ? rawAvail : [],
      accountRole: (u.accountRole ?? u.account_role ?? null) as AccountClaims['accountRole'],
      seatType: (u.seatType ?? u.seat_type ?? null) as AccountClaims['seatType'],
      planSlug: (u.planSlug ?? u.plan_slug) as string | undefined,
      planFeatures: Array.isArray(planFeatures) ? planFeatures : [],
      tenantId: (u.tenantId ?? u.tenant_id) as string | undefined,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
}

/** Aggregates the tenant-level role strings exposed on the user object. */
function useUserRoleStrings(): string[] {
  const { user } = useAuth();
  if (!user) return [];
  const roles = (user as unknown as Record<string, unknown>).roles;
  const out: string[] = [];
  if (Array.isArray(roles)) {
    for (const r of roles) if (typeof r === 'string') out.push(r);
  }
  if (typeof user.role === 'string') out.push(user.role);
  return out;
}

/**
 * Returns true when the current user is an account_owner OR a platform
 * admin (master_admin / super_admin) — the latter bypass per Wave 3 gap
 * #2 FE so Janus internal staff can reach cross-tenant pages.
 */
export function useIsAccountOwner(): boolean {
  const { accountRole } = useAccountClaims();
  const roleStrings = useUserRoleStrings();
  if (isPlatformAdmin(roleStrings)) return true;
  return accountRole === 'account_owner';
}

/**
 * Generic account-scope predicate: passes for platform admins or for users
 * whose accountRole is in the allow-list. Useful when wiring sidebar /
 * header items that should appear for owners + Janus staff.
 */
export function useHasAccountScope(
  allowedAccountRoles: ReadonlyArray<Exclude<AccountClaims['accountRole'], null>> = ['account_owner'],
): boolean {
  const { accountRole } = useAccountClaims();
  const roleStrings = useUserRoleStrings();
  if (isPlatformAdmin(roleStrings)) return true;
  return !!accountRole && allowedAccountRoles.includes(accountRole);
}
